<template>
  <div class="raft-file-listing">
    <b-table
      striped
      hover
      responsive
      show-empty
      :fields="fields"
      :items="allFiles"
      :key="componentKey"
      primary-key="id"
      ref="table"
      caption-top
      caption-html="Attached Files"
    >
      <template #empty="scope">
        <div class="raftjs-empty-text">
          {{ emptyText }}
        </div>
      </template>
      <template #cell(id)="data">
        <b-link
          v-if="allowFileDownload && data?.item?.filename"
          @click="downloadFile(data)"
          :id="'download-' + data?.item?.id"
          class="file-download"
        >
          {{ data?.item?.filename }}
          <b-img
            class="download-icon"
            :src="PathMapper.getIconAssetPath('download.png', $isB4)"
            alt="Click here to download file"
          />
        </b-link>
        <span v-else-if="data?.item?.name">
          {{ data?.item?.name }}
        </span>
        <p v-if="showFileUrl">
          <em>{{ data?.item?.uri?.url }}</em>
        </p>
      </template>
      <template #cell(filemime)="data">
        {{ data?.item?.filemime || singleFileData[0].filemime }}
      </template>
      <template #cell(filesize)="data" v-if="singleFileData">
        {{ singleFileData[0].filesize }}
      </template>
      <template v-if="allowFileRemove && !disabled" #cell(delete_action)="data">
        <b-link
          @click="deleteAttachedFile(data?.item?.id)"
          :id="'delete-' + data?.item?.id"
          class="file-delete"
        >
          Delete
        </b-link>
      </template>
    </b-table>
  </div>
</template>

<script>
import FileSaver from "file-saver";
import { useDrupalSettings } from "@/composables/useDrupalSettings";
import PathMapper from "@/lib/PathMapper";
import RaftGenericHelper from "@/lib/raft_generic_helper";
import { restClient } from "@/lib/raftApi";

const { config } = useDrupalSettings();

export default {
  name: "FileAttachmentListing",
  data() {
    return {
      deletedFiles: [],
      PathMapper: PathMapper,
    };
  },
  methods: {
    deleteAttachedFile: function (fileId) {
      this.deletedFiles.push(fileId);
      // Let the parent know that a file has been deleted.
      this.$emit("file-deleted", fileId);
    },
    async downloadFile(data) {
      let querystring = "";
      if (config.groupContext) {
        querystring = "?rog-type=node&rog-id=" + config.groupContext;
      }
      const fileData = await restClient.downloadFile(
        data.item.uri.url + querystring,
      );
      FileSaver.saveAs(new Blob([fileData]), data.item.filename);
    },
  },
  computed: {
    allFiles() {
      return this.files.filter(
        (fileObject) => !this.deletedFiles.includes(fileObject.id),
      );
    },
    componentKey() {
      return this.allFiles.length;
    },
    fields() {
      const fields = [
        {
          key: "id",
          label: "Description",
          sortable: false,
        },
        {
          key: "filemime",
          label: "Type",
          sortable: false,
        },
        {
          key: "filesize",
          label: "Size",
          sortable: false,
          formatter: (value) => RaftGenericHelper.formatBytes(value),
        },
        {
          key: "created",
          label: "Uploaded on",
          sortable: false,
          formatter: (value) => RaftGenericHelper.formatDateString(value, "/"),
        },
      ];
      if (this.allowFileRemove) {
        fields.push({
          key: "delete_action",
          label: "Remove",
        });
      }
      return fields;
    },
    singleFileData() {
      // if this.file does not contain drupal_internal__fid property,
      // create new object so file data can be displayed properly
      if (this.files.length === 1 && !this.files[0].drupal_internal__fid) {
        const formattedSize = RaftGenericHelper.formatBytes(this.files[0].size);
        return [
          {
            filemime: this.files[0].type,
            filesize: formattedSize,
            filename: this.files[0].name,
          },
        ];
      }
      return null;
    },
  },
  props: {
    files: {
      type: Array,
      default: () => [],
    },
    showFileUrl: {
      type: Boolean,
      default: false,
    },
    allowFileRemove: {
      type: Boolean,
      default: false,
    },
    allowFileDownload: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: "There are currently no attachments to list.",
    },
    singleDocument: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped>
.raft-file-listing :deep(caption) {
  text-align: left;
  font-weight: bold;
}
:deep(img.download-icon) {
  float: right;
  vertical-align: center;
}
</style>
